
import { useHandleRoutes } from "@hooks";
import { formatCurrency, parseDistanceToKm } from "@utils";
import CardMap from "./CardMap";
import LoadingSkeleton from "./LoadingSkeleton";
import PageTitle from "./PageTitle";
import SuggestionList, { formatHours } from "./SuggestionList";
import { useControll } from "./useControll";

const TripSmartRoute = () => {
  const {
    loading,
    suggestions,
    smartRoutePreference,
    handleSelectSuggestion,
    handleSkip,
    handleBack,
    routeData,
    edit,
    selectedFuelOption,
    isCreatingTrip,
  } = useControll();

  const { handleRoutes } = useHandleRoutes();
  const { segments, boundingBox, markers, position } = handleRoutes(routeData.responseHere?.routes, routeData.stops, {
    padding: 100,
  });


  const distanceNumber = parseDistanceToKm(routeData?.distance);
  const average = routeData?.routeCost?.averageConsume || 0;
  const totalConsumption = average > 0 ? distanceNumber / average : 0;

  const label = selectedFuelOption === 8 ? "kwh" : "L";
  const estimatedConsumption = average > 0 ? totalConsumption.toFixed(2).replace(/\./g, ",").replace(/\s/g, "") : "0";
  const hasSuggestion = !!routeData.routeCost?.averageConsume && !!routeData.routeCost?.fuelAverageCosts;
  const duration = routeData?.responseHere?.routes[0]?.sections?.reduce((acc: number, section: any) => acc + (section?.summary?.duration || 0), 0);
  const totalStayTimeSeconds = routeData?.stops?.reduce((sum, res) => sum + (([h, m, s]) => h * 3600 + m * 60 + s)(res.stayTime.split(":").map(Number)), 0);

  const renderMap = () => (
    <div className="display-inline-block width-100pct padding-20">
      <CardMap
        height="height-400"
        duration={formatHours(duration + totalStayTimeSeconds)}
        distance={routeData?.distance?.replace(/\./g, ",")?.replace(/\s/g, "")}
        segments={segments.length ? [{ points: segments, alternative: false }] : []}
        markers={markers}
        zoom={9}
        position={position}
        boundingBox={boundingBox}
        estimated_consumption={estimatedConsumption + label}
        totalCost={formatCurrency(routeData.routeCost?.totalCosts)}
        isPrimary
      />
    </div>
  );

  const renderSuggestions = () =>
    hasSuggestion ? (
      <>
        <PageTitle
          title="Rotas sugeridas por Inteligência Artificial"
          subtitle="Ao utilizar este recurso, você contribui para o treinamento de algoritmos de IA que otimizam rotas e processos. Quando a viagem estiver em andamento e o algoritmo identificar um caminho melhor, você será avisado para aceitar ou recusar a sugestão de melhor caminho proposta pela IA. Essa melhoria contínua apoia iniciativas de eficiência operacional e descarbonização, reduzindo emissões e consumo de recursos."
        />
        <SuggestionList
          label={label}
          isEdit={edit}
          routeData={routeData}
          suggestions={suggestions}
          onSelectSuggestion={handleSelectSuggestion}
          onSkip={handleSkip}
          onBack={handleBack}
          isCreatingTrip={isCreatingTrip}
          primary={{
            totalCost: routeData.routeCost?.totalCosts,
            distance: routeData?.distance,
            duration: (duration || 0) + (totalStayTimeSeconds || 0),
            estimatedConsumption: totalConsumption,
          }}
        />
      </>
    ) : (
      <PageTitle
        title=""
        subtitle="Ao utilizar o recurso de sugestão preenchendo os valores de Consumo do veículo (km/litro) e Preço do combustível (R$/litro), você contribui para o treinamento de algoritmos de IA que otimizam rotas e processos. Essa melhoria contínua apoia iniciativas de eficiência operacional e descarbonização, reduzindo emissões e consumo de recursos."
      />
    );

  const renderActions = () => (
    <div className="margin-top-20 display-flex justify-content-center">
      <button
        className={`btn btn-default padding-x-50 padding-y-20 font-size-18 margin-right-10 ${isCreatingTrip ? "btn-loading btn-icon-only disabled" : ""
          }`}
        onClick={!isCreatingTrip ? handleBack : undefined}
        disabled={isCreatingTrip}
      >
        {!isCreatingTrip && "Voltar"}
      </button>
      <button
        className={`btn btn-primary padding-y-20 padding-x-50 font-size-18 ${isCreatingTrip ? "btn-loading btn-icon-only disabled" : ""
          }`}
        onClick={!isCreatingTrip ? handleSkip : undefined}
        disabled={isCreatingTrip}
      >
        {!isCreatingTrip && (edit ? "Editar rota sem otimização" : "Criar rota sem otimização")}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="padding-left-50 padding-right-50 padding-bottom-50 margin-bottom-50">
        <LoadingSkeleton smartRoutePreference={smartRoutePreference} />
      </div>
    );
  }

  return (
    <div className="padding-left-50 padding-right-50 padding-bottom-50 margin-bottom-50">
      <PageTitle title="Rota sem otimização" subtitle="Essa rota não utiliza recursos de inteligência artificial." />
      {renderMap()}
      {renderSuggestions()}
      {renderActions()}
    </div>
  );
};

export default TripSmartRoute;

